import { reactive, getCurrentInstance, onMounted } from "vue";
import { useFilters } from "@/composables/filters/useFilters";
import { usePositions } from "@/composables/positions/usePositions";
import { useRoles } from "@/composables/catalogs/useRoles";
import { useHighlightRow } from "@/composables/common/useHighlightRow";
import { nextTick } from "vue";
import {
  getRolesListAvailableForPosition,
  getSingleEmployeeV2,
  recalculateEmployeeReadiness,
  getChatterList,
  updatePositionAssignment,
  getListOfDepartments,
  assignPositionToDepartment,
  releasePositionFromDepartment,
  setJobFamily,
  unassignJobFamily,
  getAccountData,
} from "@/api";
import { transformChatterData } from "@/utils/chatterUtils";
import { positionActionErrorMessage } from "@components/utils/positionErrorMessages";
import {
  getFilters as getFiltersFromStorage,
  getFilterMeta as getFilterMetaFromStorage,
  updateFilterMeta as updateFilterMetaInStorage,
} from "@components/helper/localStorageHelper";
import { getDefaultFiltersForKey } from "@components/constants/filterDefaultsRegistry";
import { getPositionFilterTokenForChipLabel } from "@components/FilterPositionsModal/positionFilterPayload";
import {
  LD_FLAG_IDEA_MDP_440,
  LD_FLAG_IDEA_MDP_551,
  LD_FLAG_IDEA_MDP_566,
  LD_FLAG_IDEA_MDP_581,
  LD_FLAG_IDEA_MDP_615,
} from "@components/constants/launchDarklyFlagKeys";
import { isProductReadinessEnabled } from "@/utils/accountProductFlags";
import {
  filterPreparingForFromPositionFilterGroups,
  filterValidityFromPositionFilterGroups,
  getPositionMultiFieldSearchFields,
  normalizeOrganizationPositionsViewSelection,
  stripPreparingForPositionFilterValues,
  stripRolesAttachedPositionFilterValues,
  stripValidityPositionFilterValues,
} from "@components/utils/preparingForOrganizationUi";

/** Default status chips for Organization > Positions tree and grid views. */
export const DEFAULT_ORGANIZATION_POSITION_STATUS_FILTERS = getDefaultFiltersForKey(
  "organizationPositions"
);

/** MDP-615 — third Organization > Positions view, next to Grid and Pane. */
export const ORGANIZATION_POSITIONS_CATEGORIZED_VIEW_SELECTION = "Categorized";

/** MDP-615 — shipping flag for the Categorized view. */
export const CATEGORIZED_VIEW_FLAG_KEY = LD_FLAG_IDEA_MDP_615;

/**
 * MDP-615 (Rule 1/10) — resolve the effective Positions view selection.
 *
 * The Categorized segment only exists while `idea-MDP-615` is on AND at least one
 * categorization column is visible; when it is hidden while selected the list
 * falls back to Grid (the shared readiness-off normalization still applies first).
 *
 * @param {string|undefined|null} selection PositionsViewSelector value.
 * @param {boolean} hasCategorizedView Whether the Categorized segment is available.
 * @returns {string} "Grid" | "Pane" | "Categorized"
 */
export function resolveOrganizationPositionsViewSelection(
  selection,
  hasCategorizedView
) {
  const normalized = normalizeOrganizationPositionsViewSelection(selection);
  if (
    normalized === ORGANIZATION_POSITIONS_CATEGORIZED_VIEW_SELECTION &&
    !hasCategorizedView
  ) {
    return normalizeOrganizationPositionsViewSelection("Grid");
  }
  return normalized;
}

/**
 * MDP-615 (Rule 8) — a row is read-only (no "+" CTA, no ×) when it is Retired or
 * Archived, or when the current Access has no edit rights on Positions.
 */
export function isCategorizedPositionReadOnly(position, canEditPositions = true) {
  const status = String(position?.status || "").toLowerCase();
  if (status === "retired" || status === "archived") return true;
  if (position?.archived === true) return true;
  return !canEditPositions;
}

/**
 * Lazy API loaders (MDP-615). Imported on first use so the composable does not pull
 * the axios/router chain into every consumer at module load.
 */
let jobCategoryApiPromise = null;
const loadJobCategoryApi = () => {
  if (!jobCategoryApiPromise) {
    jobCategoryApiPromise = import("@/api/jobCategoryAPI");
  }
  return jobCategoryApiPromise;
};

let jobFamilyResultsLoaderPromise = null;
const loadJobFamilyResultsLoader = () => {
  if (!jobFamilyResultsLoaderPromise) {
    jobFamilyResultsLoaderPromise = import(
      "@components/JobFamilyPickerDialog/loadJobFamilyPickerResults"
    ).then((mod) => mod.loadJobFamilyPickerResults);
  }
  return jobFamilyResultsLoaderPromise;
};

/**
 * ER-11707 — indicator labels that are self-scoping: their API params
 * (occupied=not_occupied / validity=not_valid) already carry the active
 * constraint server-side after ER-11704, so the status backfill must not prepend
 * Draft/Active for them. Single source of truth shared by the KPI-card click
 * (handleIndicatorSelect → resolveOrganizationPositionStatusFilters) and the
 * reload path (getFilters → resolveOrganizationPositionStatusFilters).
 */
export const SELF_SCOPING_INDICATOR_LABELS = ["Vacant", "Is not valid"];

/** Applies default status chips when none are set (except for self-scoping indicators). */
export const resolveOrganizationPositionStatusFilters = (values) => {
  const filters = Array.isArray(values) ? [...values] : [];
  const nonStatusFilters = filters.filter((value) => {
    const normalized = typeof value === "string" ? value.toLowerCase() : "";
    return !["draft", "active", "retired"].includes(normalized);
  });
  const hasStatusFilter = filters.some((value) => {
    const normalized = typeof value === "string" ? value.toLowerCase() : "";
    return ["draft", "active", "retired"].includes(normalized);
  });
  const hasSelfScopingIndicator = filters.some((value) => {
    const normalized = typeof value === "string" ? value.toLowerCase() : "";
    return SELF_SCOPING_INDICATOR_LABELS.some(
      (label) => label.toLowerCase() === normalized
    );
  });

  // Self-scoping indicators are complete on their own — prepending Draft/Active
  // here would make the reload request differ from the card-click request.
  if (hasStatusFilter || hasSelfScopingIndicator) {
    return filters;
  }

  return [...DEFAULT_ORGANIZATION_POSITION_STATUS_FILTERS, ...nonStatusFilters];
};

/**
 * Query keys used for position list search. The simple search box sets `name`; the multi-field box
 * sets `position_name` (and employee fields). Clearing only `name` leaves `position_name` applied,
 * which caused a stale API request before "Clear all" finished resetting filters.
 */
export const POSITION_LIST_SEARCH_PARAM_KEYS = [
  "name",
  "position_name",
  "primary_employee_name",
  "preparing_employee_name",
];

/**
 * MDP-551 (ER-10494): maps flat position filter values (radio labels as persisted by the legacy
 * SearchModal, or the Filter Modal Standard's lowercase snake_case slugs) to the three new Positions
 * list API params. Returns an object containing only the params that are set.
 *
 * Backend contract (ER-10493): validity=valid|not_valid, occupied=occupied|not_occupied,
 * preparing_for=has_preparing|no_preparing. The backend no-ops these when idea-MDP-551 is off.
 * ER-10517 / MDP-558 adds roles_attached=attached|not_attached.
 */
export function resolveMdp551PositionApiFilters(values = []) {
  const result = {};
  (Array.isArray(values) ? values : []).forEach((el) => {
    const value = String(el).toLowerCase();
    if (value === "is valid" || value === "is_valid") {
      result.validity = "valid";
    } else if (value === "is not valid" || value === "is_not_valid") {
      result.validity = "not_valid";
    } else if (value === "is occupied" || value === "occupied") {
      result.occupied = "occupied";
    } else if (value === "is not occupied" || value === "vacant" || value === "not_occupied") {
      result.occupied = "not_occupied";
    } else if (value === "has preparing for" || value === "has_preparing") {
      result.preparing_for = "has_preparing";
    } else if (value === "nobody prepares for" || value === "no_preparing") {
      result.preparing_for = "no_preparing";
    } else if (value === "attached" || value === "has roles attached") {
      result.roles_attached = "attached";
    } else if (value === "no role attached" || value === "not_attached") {
      result.roles_attached = "not_attached";
    }
  });
  return result;
}

/**
 * MDP-551 (ER-10494): pushes the radio group filters onto the positions store.
 * Shared by the grid list (pushFilterValuesToPositionsStore) and the pane tree view
 * (OrganizationPositionsTab), so one code path owns the store contract.
 */
export function applyMdp551PositionFiltersToPositionsStore(positions, values = []) {
  Object.entries(resolveMdp551PositionApiFilters(values)).forEach(([key, value]) => {
    positions.addPositionFilter(key, value);
  });
}

let instance = null;

export function useOrganizationPositionsViewState() {
  // Check if we're in Storybook context (window.location includes storybook)
  const isStorybook =
    typeof window !== "undefined" &&
    (window.location?.href?.includes("localhost:6006") ||
      window.location?.href?.includes("storybook") ||
      window.__STORYBOOK_ENV__);

  // Only use singleton in non-Storybook contexts. ER-12338: the singleton
  // short-circuits before the onMounted hook below, so a second mount in the same
  // SPA session never re-ran initializeFilters. Re-register the categorization
  // refresh on every mount so a Settings toggle (job_categories_on /
  // job_families_on) is picked up on the next navigation to Positions.
  if (!isStorybook && instance) {
    onMounted(async () => {
      await instance.refreshCategorizationSettings?.();
    });
    return instance;
  }

  const positions = usePositions();
  const { getEmployeeAvatar } = usePositions();
  const filter = useFilters();
  const roles = useRoles();
  const { highlight } = useHighlightRow();
  let app = null;
  let proxy = null;
  try {
    app = getCurrentInstance();
    proxy = app?.proxy;
  } catch (e) {
    // In Storybook or other contexts, getCurrentInstance might not be available
    console.warn("getCurrentInstance not available:", e);
  }

  const state = reactive({
    // ER-11707: idea-MDP-551 gate. Read by initializeFilters and consumed by
    // pushFilterValuesToPositionsStore so KPI-card clicks honour the same flag
    // that hides the Validity/Occupied radio groups in the filter modal.
    mdp551Enabled: false,
    filters: {
      values: [...DEFAULT_ORGANIZATION_POSITION_STATUS_FILTERS],
      // Grid list view department filter (MDP-440).
      department: null,
      departmentScope: "subtree",
      // Pane tree view department filter — independent from grid.
      paneDepartment: null,
      paneDepartmentScope: "subtree",
      searchText: "",
      multipleFieldSearchValues: [], // Store multiple field search values
      showFilterDialog: false,
      key: "organizationPositions",
      clearSelection: "",
      _displayValueMap: [], // Map from display values to original filter values for removal
      statusOptions: ["Draft", "Active", "Retired"],
      filterGroups: [
        // MDP-551 (ER-10494): Validity / Occupied / Preparing for radio groups.
        // Shown only while idea-MDP-551 is on (see initializeFilters); the backend
        // ignores their query params when the flag is off. "No Filter" is the
        // default (RadioInput's sentinel — never persisted as a chip).
        {
          label: "Validity",
          items: [
            { label: "No Filter", value: "No Filter" },
            { label: "Is valid", value: "Is valid" },
            { label: "Is not valid", value: "Is not valid" },
          ],
        },
        {
          label: "Occupied",
          items: [
            { label: "No Filter", value: "No Filter" },
            { label: "Is occupied", value: "Is occupied" },
            { label: "Vacant", value: "Vacant" },
          ],
        },
        {
          label: "Preparing for",
          items: [
            { label: "No Filter", value: "No Filter" },
            { label: "Has Preparing for", value: "Has Preparing for" },
            { label: "Nobody prepares for", value: "Nobody prepares for" },
          ],
        },
        {
          label: "Archived",
          items: [
            { label: "Do not Show", value: "Do not Show" },
            { label: "Show", value: "Show" },
          ],
        },
      ],
      // groupsItems: [
      //   {
      //     name: "To be",
      //     groups: [
      //       {
      //         type: "check",
      //         label: "Status",
      //         items: [
      //           { label: "Draft", value: "Draft" },
      //           { label: "Active", value: "Active" },
      //           { label: "Retired", value: "Retired" },
      //           { label: "Inactive", value: "Inactive" },
      //         ],
      //       },
      //       {
      //         type: "radio",
      //         label: "Occupied",
      //         items: [
      //           { label: "No Filter", value: "No Filter" },
      //           { label: "Occupied", value: "Occupied" },
      //           { label: "Not Occupied", value: "Not Occupied" },
      //         ],
      //       },
      //       {
      //         type: "radio",
      //         label: "Preparing For",
      //         items: [
      //           { label: "No Filter", value: "No Filter" },
      //           { label: "Has Candidates", value: "Has Candidates" },
      //           { label: "Nobody", value: "Nobody" },
      //         ],
      //       },
      //       {
      //         type: "radio",
      //         label: "Timeline",
      //         items: [
      //           { label: "No Filter", value: "No Filter" },
      //           { label: "Recently Activated", value: "Recently Activated" },
      //           { label: "Recently Retired", value: "Recently Retired" },
      //           { label: "Recently Created", value: "Recently Created" },
      //         ],
      //       },
      //       {
      //         type: "radio",
      //         label: "Archived",
      //         items: [
      //           { label: "Do not Show", value: "Do not Show" },
      //           { label: "Show", value: "Show" },
      //         ],
      //       },
      //     ],
      //   },
      // ],
    },
    pagination: {
      page: 1,
      perPage: 10,
    },
    sortField: "position_name",
    sortDescending: false,
    config: {
      hideTopSection: true,
      hideActiveNo: false,
      activeRecordsCount: null,
      actionLabel: "Add Position",
      title: "Positions Organization",
      positionsViewSelection: "Grid",
      showSearchPositionByMultipleFieldBox: true,
      searchPositionByMultipleFields: [
        { key: "position_name", label: "Position name" },
        { key: "primary_employee_name", label: "Assigned as Primary" },
        { key: "preparing_employee_name", label: "Assigned to Prepare" },
      ],
    },
    /**
     * MDP-615 — Categorized view (Department / Job Category / Job Family columns).
     * Flags and account toggles decide which columns render; the pickers below own
     * the assign/unassign flows triggered from a row.
     */
    categorization: {
      ideaMdp615Enabled: false,
      departmentFlagEnabled: false,
      jobCategoryFlagEnabled: false,
      jobFamilyFlagEnabled: false,
      jobCategoriesOn: false,
      jobFamiliesOn: false,
      /** Access with no edit rights on Positions → every tag renders read-only (Rule 8). */
      canEditPositions: true,
      departmentPicker: {
        show: false,
        position: null,
        tree: null,
        loading: false,
        assigning: false,
        error: "",
      },
      jobCategoryPicker: {
        show: false,
        position: null,
        results: [],
        loading: false,
        assigning: false,
        selection: null,
        error: "",
      },
      jobFamilyPicker: {
        show: false,
        position: null,
        results: [],
        loading: false,
        assigning: false,
        selection: null,
        error: "",
      },
      /** Shared unassign confirmation dialog (department / job-category / job-family). */
      detachDialog: {
        show: false,
        kind: "",
        position: null,
        parentName: "",
      },
    },
    // Modal states - same as usePositionViewState
    archiveAction: null,
    addPositionModal: {
      show: false,
    },
    activatePositionModal: {
      show: false,
    },
    deletePositionModal: {
      show: false,
      message: "",
    },
    renamePositionModal: {
      show: false,
      errorMessage: "",
    },
    attachRoleModal: {
      show: false,
      roles: [],
      attachedRoles: [],
      selectedRoleId: null,
      page: 1,
      isLoadingMore: false,
      hasMoreData: true,
      currentSearchTerm: "",
      currentPositionId: null,
    },
    relieveAssignment: {
      show: false,
      currentPosition: null,
      assignedEmployee: null,
      type: "",
    },
    archivePositionModal: {
      show: false,
      errorMessage: "",
    },
    retirePositionModal: {
      show: false,
    },
    reactivatePositionModal: {
      show: false,
    },
    clonePositionModal: {
      show: false,
    },
    openChatterModal: {
      show: false,
      chatterData: [],
      totalPositionChatter: 0,
      page: 1,
      recordId: null,
      type: "position",
      typeValue: "",
      isEmployeeChatter: false,
      avatar: {
        picture: "",
        firstName: "",
        lastName: "",
      },
    },
    openDefault: false,
    currentPosition: null,
  });

  const normalizeFilterScalar = (el) => {
    if (typeof el === "string") return el;
    if (el && typeof el === "object") {
      const v = el.value ?? el.label;
      if (v != null && v !== "") return v;
    }
    return null;
  };

  /**
   * Syncs positions API dynamic filters from saved filter values (state or argument).
   * Used after restoring from localStorage and when applying filters in the table.
   */
  const pushFilterValuesToPositionsStore = (rawValues) => {
    const values = rawValues != null ? rawValues : state.filters.values;

    positions.clearPositionDynamicFilters();

    const flattened = (Array.isArray(values) ? values : [])
      .map(normalizeFilterScalar)
      .filter(Boolean);

    const statusOptions = ["draft", "active", "retired"];
    const statusFilters = [
      ...new Set(
        flattened
          .filter((el) => statusOptions.includes(String(el).toLowerCase()))
          .map((el) => String(el).toLowerCase())
      ),
    ];

    statusFilters.forEach((status) => {
      positions.addPositionFilter("status", status);
    });

    let archivedFilterApplied = false;
    flattened.forEach((el) => {
      if (el === "Show" || el === "show_archived") {
        archivedFilterApplied = true;
        positions.addPositionFilter("archived", true);
      } else if (el === "Do not Show" || el === "no_archived") {
        archivedFilterApplied = true;
        positions.addPositionFilter("archived", false);
      }
    });

    if (!archivedFilterApplied) {
      positions.addPositionFilter("archived", false);
    }

    // MDP-551 (ER-10494): Validity / Occupied / Preparing-for radio groups.
    // The legacy modal persists radio items by LABEL; the Filter Modal Standard
    // uses lowercase snake_case slugs — the helper accepts both so persisted state
    // survives flag flips.
    // ER-11707: only apply when the idea-MDP-551 flag is on — with the flag off the
    // modal hides these groups, so a KPI-card click must not apply params the user
    // cannot see or clear.
    if (state.mdp551Enabled) {
      applyMdp551PositionFiltersToPositionsStore(positions, flattened);
    }

    if (state.filters.department?.id != null) {
      positions.addPositionFilter("department", state.filters.department.id);
      positions.addPositionFilter(
        "include_subdepartments",
        state.filters.departmentScope !== "direct"
      );
    }
  };

  /**
   * Update + persist a department filter (object) and scope ("direct" | "subtree").
   * @param {"grid"|"pane"} view — grid list vs pane tree; each view keeps its own selection.
   */
  const setDepartmentFilter = (department, scope = "subtree", view = "grid") => {
    const normalizedScope = department ? scope || "subtree" : "subtree";
    if (view === "pane") {
      state.filters.paneDepartment = department || null;
      state.filters.paneDepartmentScope = normalizedScope;
    } else {
      state.filters.department = department || null;
      state.filters.departmentScope = normalizedScope;
    }
    updateFilterMetaInStorage(state.filters.key, {
      department: state.filters.department,
      departmentScope: state.filters.departmentScope,
      paneDepartment: state.filters.paneDepartment,
      paneDepartmentScope: state.filters.paneDepartmentScope,
    });
  };

  // ------------------------------------------------------------------------
  // MDP-615 — Positions Categorized view (Department / Job Category / Job Family)
  // ------------------------------------------------------------------------

  /**
   * Whether at least one categorization column is visible. Department follows
   * idea-MDP-440; Job Category follows `account.job_categories_on` + idea-MDP-566;
   * Job Family follows `account.job_families_on` + idea-MDP-581 (Rule 4/13).
   */
  const hasCategorizationColumns = () => {
    const c = state.categorization;
    const jobCategory = c.jobCategoriesOn && c.jobCategoryFlagEnabled;
    const jobFamily = c.jobFamiliesOn && c.jobFamilyFlagEnabled;
    return c.departmentFlagEnabled || jobCategory || jobFamily;
  };

  /** The Categorized segment exists only while idea-MDP-615 is on AND a column is visible. */
  const hasCategorizedView = () =>
    state.categorization.ideaMdp615Enabled && hasCategorizationColumns();

  /** Persist the view selection, falling back to Grid when the segment is hidden. */
  const setPositionsViewSelection = (selection) => {
    const effective = resolveOrganizationPositionsViewSelection(
      selection,
      hasCategorizedView()
    );
    state.config.positionsViewSelection = effective;
    return effective;
  };

  /** True when an access cannot edit Positions, overriding row editability (Rule 8). */
  const setCanEditPositions = (canEdit) => {
    state.categorization.canEditPositions = canEdit !== false;
  };

  const isRowReadOnly = (position) =>
    isCategorizedPositionReadOnly(
      position,
      state.categorization.canEditPositions
    );

  /** Read `job_categories_on` / `job_families_on` from the account (ER-11633 pattern). */
  const loadCategorizationSettings = async () => {
    const accountId = localStorage.getItem("currentAccountID");
    if (!accountId) return;
    try {
      const { data } = await getAccountData(accountId);
      state.categorization.jobCategoriesOn = data?.job_categories_on === true;
      state.categorization.jobFamiliesOn = data?.job_families_on === true;
    } catch {
      state.categorization.jobCategoriesOn = false;
      state.categorization.jobFamiliesOn = false;
    }
  };

  /**
   * Reload the current page with the current filters/search/sort. The positions
   * store already carries all three, so a plain reload keeps the list state (Rule 2).
   */
  const reloadCategorizedPositions = async () => {
    await positions.loadAllPositions();
  };

  // --- Cell value adapters ------------------------------------------------

  /** List row department (nested `sub_department`) → DepartmentTagInPositionForm {id, path}. */
  const getCategorizedDepartment = (position) => {
    const department =
      position?.position?.department || position?.department || null;
    if (!department) return null;
    // Already a mapped { id, path } tag shape.
    if (Array.isArray(department.path)) {
      return { id: department.id, path: department.path };
    }
    const path = [];
    let current = department;
    let leafId = department.id;
    while (current) {
      if (current.name) path.push(current.name);
      leafId = current.id != null ? current.id : leafId;
      current = current.sub_department ?? null;
    }
    return { id: leafId, path };
  };

  const getCategorizedJobCategory = (position) =>
    position?.job_category || null;

  const getCategorizedJobFamily = (position) => position?.job_family || null;

  const getCategorizedJobFamilyLevel = (position) =>
    position?.job_family_level || null;

  // --- Department assignment ---------------------------------------------

  const openCategorizedDepartmentPicker = async (position) => {
    const picker = state.categorization.departmentPicker;
    picker.show = true;
    picker.position = position;
    picker.error = "";
    if (picker.tree !== null) return;
    picker.loading = true;
    try {
      const accountId = localStorage.getItem("currentAccountID");
      const { data } = await getListOfDepartments(
        accountId,
        "",
        { status: ["active", "draft"] }
      );
      picker.tree = Array.isArray(data) ? data : data?.results || [];
    } catch {
      picker.tree = [];
    } finally {
      picker.loading = false;
    }
  };

  const closeCategorizedDepartmentPicker = () => {
    const picker = state.categorization.departmentPicker;
    picker.show = false;
    picker.position = null;
    picker.error = "";
  };

  const assignCategorizedDepartment = async ({ department } = {}) => {
    const picker = state.categorization.departmentPicker;
    const accountId = localStorage.getItem("currentAccountID");
    const position = picker.position;
    if (!accountId || !position?.id || !department?.id || picker.assigning) {
      return;
    }
    picker.assigning = true;
    picker.error = "";
    try {
      await assignPositionToDepartment(accountId, department.id, position.id);
      closeCategorizedDepartmentPicker();
      await reloadCategorizedPositions();
    } catch (error) {
      // Rule 10: keep the dialog open and surface the failure inline.
      console.error("Error assigning department from categorized row:", error);
      picker.error = "Failed to assign position to department.";
    } finally {
      picker.assigning = false;
    }
  };

  const unassignCategorizedDepartment = async (position) => {
    const accountId = localStorage.getItem("currentAccountID");
    const departmentId = getCategorizedDepartment(position)?.id;
    if (!accountId || !position?.id || !departmentId) return;
    await releasePositionFromDepartment(accountId, departmentId, {
      position_id: position.id,
    });
    await reloadCategorizedPositions();
  };

  // --- Job Category assignment -------------------------------------------

  const mapCategorizedJobCategoryResult = (category) => ({
    jobCategoryId: category?.id,
    name: String(category?.name || "").trim(),
    code: category?.code_str || category?.code || "",
    status: category?.status || "",
    totalScore: category?.total_score ?? null,
  });

  const loadCategorizedJobCategories = async (query = "") => {
    const picker = state.categorization.jobCategoryPicker;
    const accountId = localStorage.getItem("currentAccountID");
    if (!accountId) {
      picker.results = [];
      picker.loading = false;
      return;
    }
    picker.loading = true;
    try {
      const { getListOfJobCategories } = await loadJobCategoryApi();
      const { data } = await getListOfJobCategories(accountId, {
        search: typeof query === "string" ? query : "",
        status: ["draft", "active", "retired"],
      });
      const rows = Array.isArray(data?.results) ? data.results : [];
      picker.results = rows.map(mapCategorizedJobCategoryResult);
    } catch {
      picker.results = [];
    } finally {
      picker.loading = false;
    }
  };

  const openCategorizedJobCategoryPicker = (position) => {
    const picker = state.categorization.jobCategoryPicker;
    picker.show = true;
    picker.position = position;
    picker.error = "";
    picker.selection = null;
    picker.results = [];
    loadCategorizedJobCategories("");
  };

  const closeCategorizedJobCategoryPicker = () => {
    const picker = state.categorization.jobCategoryPicker;
    picker.show = false;
    picker.position = null;
    picker.error = "";
    picker.selection = null;
  };

  const categorizedJobCategoryErrorMessage = (error) => {
    const code = error?.response?.data?.error;
    if (code === "JOB_CATEGORY_NOT_ACTIVE") {
      return "Only Active job categories can be assigned.";
    }
    const detail = error?.response?.data?.details || error?.response?.data?.detail;
    if (typeof detail === "string" && detail.trim()) return detail;
    return "Failed to assign job category.";
  };

  const assignCategorizedJobCategory = async ({ jobCategoryId } = {}) => {
    const picker = state.categorization.jobCategoryPicker;
    const accountId = localStorage.getItem("currentAccountID");
    const position = picker.position;
    if (!accountId || !position?.id || !jobCategoryId || picker.assigning) return;
    picker.assigning = true;
    picker.error = "";
    try {
      const { assignPositionJobCategory, unassignPositionJobCategory } =
        await loadJobCategoryApi();
      // Rule 6: unassign before reassign — close the present period first.
      if (position.job_category) {
        await unassignPositionJobCategory(accountId, position.id);
      }
      await assignPositionJobCategory(accountId, position.id, {
        assignment_type: "SUBSCRIBED",
        job_category_id: jobCategoryId,
      });
      closeCategorizedJobCategoryPicker();
      await reloadCategorizedPositions();
    } catch (error) {
      // Rule 10: keep the dialog open with the inline error.
      console.error("Error assigning job category from categorized row:", error);
      picker.error = categorizedJobCategoryErrorMessage(error);
    } finally {
      picker.assigning = false;
    }
  };

  const unassignCategorizedJobCategory = async (position) => {
    const accountId = localStorage.getItem("currentAccountID");
    if (!accountId || !position?.id) return;
    const { unassignPositionJobCategory } = await loadJobCategoryApi();
    await unassignPositionJobCategory(accountId, position.id);
    await reloadCategorizedPositions();
  };

  // --- Job Family assignment ---------------------------------------------

  const loadCategorizedJobFamilies = async (query = "") => {
    const picker = state.categorization.jobFamilyPicker;
    const accountId = localStorage.getItem("currentAccountID");
    if (!accountId) {
      picker.results = [];
      picker.loading = false;
      return;
    }
    picker.loading = true;
    try {
      const loadJobFamilyPickerResults = await loadJobFamilyResultsLoader();
      picker.results = await loadJobFamilyPickerResults(accountId, query);
    } catch {
      picker.results = [];
    } finally {
      picker.loading = false;
    }
  };

  const openCategorizedJobFamilyPicker = (position) => {
    const picker = state.categorization.jobFamilyPicker;
    picker.show = true;
    picker.position = position;
    picker.error = "";
    picker.selection = null;
    picker.results = [];
    loadCategorizedJobFamilies("");
  };

  const closeCategorizedJobFamilyPicker = () => {
    const picker = state.categorization.jobFamilyPicker;
    picker.show = false;
    picker.position = null;
    picker.error = "";
    picker.selection = null;
  };

  const assignCategorizedJobFamily = async ({ jobFamilyId, levelId } = {}) => {
    const picker = state.categorization.jobFamilyPicker;
    const accountId = localStorage.getItem("currentAccountID");
    const position = picker.position;
    if (
      !accountId ||
      !position?.id ||
      !jobFamilyId ||
      !levelId ||
      picker.assigning
    ) {
      return;
    }
    picker.assigning = true;
    picker.error = "";
    try {
      // Rule 6: unassign before reassign — a Position subscribes to one family.
      if (position.job_family) {
        await unassignJobFamily(accountId, position.id);
      }
      await setJobFamily(accountId, position.id, {
        job_family_id: jobFamilyId,
        job_family_level_id: levelId,
      });
      closeCategorizedJobFamilyPicker();
      await reloadCategorizedPositions();
    } catch (error) {
      // Rule 10: keep the dialog open with the inline error.
      console.error("Error assigning job family from categorized row:", error);
      picker.error = "Failed to assign job family.";
    } finally {
      picker.assigning = false;
    }
  };

  const unassignCategorizedJobFamily = async (position) => {
    const accountId = localStorage.getItem("currentAccountID");
    if (!accountId || !position?.id) return;
    await unassignJobFamily(accountId, position.id);
    await reloadCategorizedPositions();
  };

  // --- Shared unassign confirmation (department / job category / job family) ---

  const openCategorizedUnassignDialog = (kind, position, parentName = "") => {
    const dialog = state.categorization.detachDialog;
    dialog.show = true;
    dialog.kind = kind;
    dialog.position = position;
    dialog.parentName = parentName || "";
  };

  const closeCategorizedUnassignDialog = () => {
    const dialog = state.categorization.detachDialog;
    dialog.show = false;
    dialog.kind = "";
    dialog.position = null;
    dialog.parentName = "";
  };

  const confirmCategorizedUnassign = async () => {
    const dialog = state.categorization.detachDialog;
    const position = dialog.position;
    if (!position?.id) return;
    proxy?.$loader?.setAppLoading?.(true);
    try {
      if (dialog.kind === "department") {
        await unassignCategorizedDepartment(position);
      } else if (dialog.kind === "job-category") {
        await unassignCategorizedJobCategory(position);
      } else if (dialog.kind === "job-family") {
        await unassignCategorizedJobFamily(position);
      }
      closeCategorizedUnassignDialog();
    } catch (error) {
      // Keep the confirmation open so the action can be retried.
      console.error("Error unassigning categorization from row:", error);
    } finally {
      proxy?.$loader?.setAppLoading?.(false);
    }
  };

  // Capacity Management (idea-MDP-636): the card has already limited the change to a vacant, Active position.
  const handleCapacityChange = async (capacity, position) => {
    if (!position?.id) return;
    const accountId = localStorage.getItem("currentAccountID");
    proxy?.$loader?.setAppLoading?.(true);
    try {
      try {
        await positions.updateCapacity({
          accountId,
          positionId: position.id,
          capacity,
          name: position.name,
        });
      } catch (error) {
        // Nothing was changed optimistically, so the card keeps the previous capacity.
        console.error("Error changing position capacity:", error);
        proxy?.$notifications?.error(positionActionErrorMessage(error, "capacity"));
      }
      // Reload either way: a rejected change usually means the position was just assigned.
      await positions.loadAllPositions();
    } finally {
      proxy?.$loader?.setAppLoading?.(false);
    }
  };

  // Filter Operations
  const applyFilters = async (filters) => {
    if (!filters || !Array.isArray(filters)) {
      return;
    }

    const newFilters = filters
      .map((item) => {
        // Handle string items (status checkboxes)
        if (typeof item === "string") {
          return item;
        }
        // Handle object items
        if (item && typeof item === "object") {
          // For radio buttons, preserve full object with labelType and value
          if (item.labelType !== null && item.labelType !== undefined) {
            const filterValue =
              item.value !== null && item.value !== undefined ? item.value : item.label;
            // Don't store "No Filter" values - they're the default and don't need to be tracked
            if (filterValue === "No Filter") {
              return null;
            }
            return {
              labelType: item.labelType,
              value: filterValue,
              label: item.label,
            };
          }
          // For checkboxes, they have label and value - return as string for status
          if (item.value !== null && item.value !== undefined) {
            return item.value;
          }
          // Fallback to label if value is not present
          if (item.label !== null && item.label !== undefined) {
            return item.label;
          }
        }
        return null;
      })
      .filter(Boolean); // Remove null/undefined values

    // Create new array reference to trigger reactivity
    state.filters.values = [...newFilters];

    filter.setFilters({
      key: state.filters.key,
      filters: newFilters,
    });

    state.filters.showFilterDialog = false;
    // Reset to first page when filters change
    state.pagination.page = 1;
  };

  const removeFilters = (removedFilter) => {
    // removedFilter is the value from SearchBar's chipText array
    // SearchBar's filteredChipText maps "Show" to "Show Archived" for display,
    // but the actual chipText still has "Show", so removedFilter will be "Show" if it was "Show Archived"
    // Handle "Show Archived" special case
    const filterToRemove =
      removedFilter === "Show Archived" ? "Show" : getPositionFilterTokenForChipLabel(removedFilter);

    // Use the display value map to find the exact original filter value
    let originalFilterToRemove = null;

    if (state.filters._displayValueMap && Array.isArray(state.filters._displayValueMap)) {
      // Find the original filter value that corresponds to this display value
      // SearchBar might emit either the display value or the original value
      const foundOriginal = state.filters._displayValueMap.find((originalVal) => {
        if (typeof originalVal === "string") {
          // String filter - match directly
          return originalVal === filterToRemove || originalVal === removedFilter;
        } else if (originalVal && typeof originalVal === "object") {
          // Object filter - match by display value
          const displayValue = originalVal.value || originalVal.label || "";
          return displayValue === filterToRemove || displayValue === removedFilter;
        }
        return false;
      });

      if (foundOriginal) {
        originalFilterToRemove = foundOriginal;
      }
    }

    // Remove the filter from values
    const updatedFilters = state.filters.values.filter((val) => {
      // If we found the exact original filter, use that for comparison
      if (originalFilterToRemove !== null && originalFilterToRemove !== undefined) {
        if (typeof originalFilterToRemove === "string" && typeof val === "string") {
          return val !== originalFilterToRemove;
        } else if (
          originalFilterToRemove &&
          typeof originalFilterToRemove === "object" &&
          val &&
          typeof val === "object"
        ) {
          // Deep comparison for objects - match by labelType and value
          const originalLabelType = originalFilterToRemove.labelType;
          const originalValue = originalFilterToRemove.value || originalFilterToRemove.label;
          const valLabelType = val.labelType;
          const valValue = val.value || val.label;
          return !(originalLabelType === valLabelType && originalValue === valValue);
        }
      }

      // Fallback: match by display value
      if (typeof val === "string") {
        // String filter (status) - direct comparison
        return val !== filterToRemove && val !== removedFilter;
      } else if (val && typeof val === "object") {
        // Object filter (radio button) - compare display value
        const filterValue = val.value || val.label || "";
        const filterLabel = val.label || val.value || "";
        return (
          filterValue !== filterToRemove &&
          filterLabel !== filterToRemove &&
          filterValue !== removedFilter &&
          filterLabel !== removedFilter
        );
      }
      return true;
    });

    // Update state with new array to trigger reactivity
    state.filters.values = [...updatedFilters];

    filter.setFilters({
      key: state.filters.key,
      filters: updatedFilters,
    });

    state.filters.clearSelection = filterToRemove;
    // Reset to first page when filter is removed
    state.pagination.page = 1;
  };

  const clearFilters = async () => {
    state.filters.values = filter.resetToDefaults(state.filters.key);
    await applyFilters(state.filters.values);
  };

  const clearSearch = async () => {
    state.filters.searchText = "";
  };

  // Search Operations
  const debounce = (func, delay) => {
    let timeout;
    return function (...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), delay);
    };
  };

  const performSearch = async (string) => {
    POSITION_LIST_SEARCH_PARAM_KEYS.forEach((key) => {
      positions.removePositionFilter(key);
    });

    // Only add search filters and load positions if there's actually a search term
    if (string && string.trim() !== "") {
      positions.addPositionFilter("name", string);
    }
    positions.setPaginationDataOffset(0);
    state.filters.searchText = string;
    state.pagination.page = 1;
    await positions.loadAllPositions();
  };

  const debouncedSearch = debounce(performSearch, 500);

  const handleSearch = (string) => {
    const queryString = string;
    debouncedSearch(queryString);
  };

  // Pagination and Sorting
  const handleRequest = async ({ page, itemsPerPage }) => {
    // Handle frontend pagination
    if (page) {
      state.pagination.page = page;
    }
    if (itemsPerPage) {
      state.pagination.perPage = itemsPerPage;
    }
  };

  const handleSort = async (sortData) => {
    // Handle frontend sorting
    if (sortData) {
      state.sortField = sortData.field || "position_name";
      state.sortDescending = sortData.descending || false;
      // Reset to first page when sorting changes
      state.pagination.page = 1;
    }
  };

  // Position Operations
  const handlePositionDropdownOperation = async (operation, position) => {
    if (!operation || !position) return;

    positions.setCurrentPosition(position);
    state.currentPosition = position;

    switch (operation.value) {
      case "activate_action":
        state.activatePositionModal.show = true;
        break;
      case "delete_action":
        state.deletePositionModal.show = true;
        break;
      case "rename_action":
        state.renamePositionModal.errorMessage = "";
        state.renamePositionModal.show = true;
        break;
      case "attach_position_role":
        await prepareAttachRole();
        state.openDefault = true;
        break;
      case "archive_action":
        state.archiveAction = "archive";
        state.archivePositionModal.errorMessage = "";
        state.archivePositionModal.show = true;
        break;
      case "unarchive_action":
        state.archiveAction = "unarchive";
        state.archivePositionModal.errorMessage = "";
        state.archivePositionModal.show = true;
        break;
      case "retire_action":
        state.retirePositionModal.show = true;
        break;
      case "reactivate_action":
        state.reactivatePositionModal.show = true;
        break;
      case "cloned_action":
        state.clonePositionModal.show = true;
        break;
    }
  };

  // Position Operation Handler - same as usePositionViewState
  const handlePositionOperation = async (operation, accountId, payload = null) => {
    try {
      proxy?.$loader?.setAppLoading(true);
      let newPosition;
      let clonedPosition;

      switch (operation) {
        case positions.operations.CREATE_POSITION:
          if (!payload) return;
          newPosition = await positions.createPosition({
            accountId,
            name: payload,
          });

          if (newPosition?.id) {
            state.addPositionModal.show = false;
            proxy?.$loader?.setAppLoading(false);
            await nextTick();

            // Navigate to position detail page using the same route as OrganizationPositionsTab
            proxy?.$router
              ?.push({
                name: "PositionDetailChild",
                params: { positionId: newPosition.id },
                query: { positionTab: "attached-roles" },
              })
              .catch(() => {});
            return;
          }

          state.addPositionModal.show = false;
          break;

        case positions.operations.ACTIVATE_POSITION:
          await positions.activate({
            accountId,
            position: positions.getCurrentPosition(),
          });
          state.activatePositionModal.show = false;
          await positions.loadAllPositions();
          break;

        case positions.operations.DELETE_POSITION: {
          const positionToDelete = positions.getCurrentPosition();
          const positionName = (positionToDelete?.name || "").trim();
          await positions.deletePosition({
            accountId,
            position: positionToDelete,
          });
          state.deletePositionModal.show = false;
          proxy?.$notifications?.success?.(
            "Position successfully deleted",
            positionName
              ? `Position ${positionName} was successfully deleted`
              : "The position was successfully deleted."
          );
          await positions.loadAllPositions();
          break;
        }

        case positions.operations.RENAME_POSITION:
          if (!payload) return;
          try {
            await positions.rename({
              accountId,
              position: positions.getCurrentPosition(),
              newName: payload,
            });
            state.renamePositionModal.show = false;
            state.renamePositionModal.errorMessage = "";
            await positions.loadAllPositions();
          } catch (error) {
            // MDP-608: keep the dialog open and render the failure inline.
            // Returning leaves currentPosition set so "Save" can retry.
            state.renamePositionModal.errorMessage = positionActionErrorMessage(
              error,
              "rename"
            );
            return;
          }
          break;

        case positions.operations.ARCHIVE_POSITION: {
          const action = state.archiveAction;
          try {
            if (action === "archive") {
              await positions.archive({
                accountId,
                position: positions.getCurrentPosition(),
              });
            } else if (action === "unarchive") {
              await positions.unarchive({
                accountId,
                position: positions.getCurrentPosition(),
              });
            }
            state.archivePositionModal.show = false;
            state.archivePositionModal.errorMessage = "";
            if (action === "unarchive") {
              proxy?.$notifications?.success?.("Position unarchived");
            }
            await positions.loadAllPositions();
          } catch (error) {
            // MDP-608: keep the dialog open; the loader is cleared in finally.
            // Returning leaves currentPosition set so "Retry" can re-run.
            state.archivePositionModal.errorMessage = positionActionErrorMessage(
              error,
              action
            );
            return;
          }
          break;
        }

        case positions.operations.RETIRE_POSITION:
          await positions.retire({
            accountId,
            position: positions.getCurrentPosition(),
          });
          state.retirePositionModal.show = false;
          await positions.loadAllPositions();
          break;

        case positions.operations.REACTIVATE_POSITION:
          await positions.reactivate({
            accountId,
            position: positions.getCurrentPosition(),
          });
          state.reactivatePositionModal.show = false;
          await positions.loadAllPositions();
          break;

        case positions.operations.CLONE_POSITION: {
          clonedPosition = await positions.clonePosition({
            accountId,
            position: positions.getCurrentPosition(),
          });
          state.clonePositionModal.show = false;
          state.filters.searchText = clonedPosition.code_str;
          performSearch(clonedPosition.code_str);
          setTimeout(() => {
            highlight({ id: clonedPosition.id, type: "Positions" });
          }, 1000);
          await roles.loadAllRoles();
          break;
        }
      }
      positions.setCurrentPosition(null);
    } catch (error) {
      // Rename / archive render their failures inline; no popup is mounted here.
      console.error("Error in position operation:", error);
    } finally {
      proxy?.$loader?.setAppLoading(false);
    }
  };

  // Attach Role Methods
  const prepareAttachRole = async () => {
    const currentPosition = positions.getCurrentPosition();
    if (!currentPosition?.id) return;
    state.attachRoleModal.currentPositionId = currentPosition.id;
    await getListOfRoles(currentPosition.id, 1, "", false);
    state.attachRoleModal.show = true;
  };

  const getListOfRoles = async (positionId, page = 1, searchTerm = "", isLoadMore = false) => {
    const limit = 10;
    const offset = (page - 1) * limit;
    const name = searchTerm || "";
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    try {
      if (isLoadMore) {
        state.attachRoleModal.isLoadingMore = true;
      }

      const attachableeRoles = await getRolesListAvailableForPosition(
        accountId,
        positionId,
        limit,
        offset,
        name
      );

      const newItems = attachableeRoles.data.results.map((item) => ({
        id: item.id,
        code: item.code_str.match(/\d+/)[0],
        name: item.name,
        status: item.status,
        attached: item?.attached_to_position,
      }));

      if (isLoadMore && state.attachRoleModal.roles) {
        state.attachRoleModal.roles = [...state.attachRoleModal.roles, ...newItems];
      } else {
        state.attachRoleModal.roles = newItems;
      }

      state.attachRoleModal.page = page;
      state.attachRoleModal.totalCount = attachableeRoles.data.count || 0;
      state.attachRoleModal.hasMoreData = page * limit < state.attachRoleModal.totalCount;
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      if (isLoadMore) {
        state.attachRoleModal.isLoadingMore = false;
      }
    }
  };

  const loadMoreRoles = async () => {
    if (state.attachRoleModal.isLoadingMore || !state.attachRoleModal.hasMoreData) {
      return;
    }
    const nextPage = state.attachRoleModal.page + 1;
    await getListOfRoles(
      state.attachRoleModal.currentPositionId,
      nextPage,
      state.attachRoleModal.currentSearchTerm,
      true
    );
  };

  const searchRoles = async (searchTerm) => {
    state.attachRoleModal.currentSearchTerm = searchTerm;
    state.attachRoleModal.page = 1;
    state.attachRoleModal.hasMoreData = true;
    await getListOfRoles(state.attachRoleModal.currentPositionId, 1, searchTerm, false);
  };

  const handleRoleSelected = (role) => {
    if (role?.id) {
      state.attachRoleModal.selectedRoleId = role.id;
    }
  };

  const handleAddRoleSelected = async (data) => {
    proxy?.$loader?.show();
    try {
      const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
      const newRole = await roles.saveNewRole({
        accountId: accountId,
        roleName: data.name,
      });
      if (newRole) {
        state.attachRoleModal.selectedRoleId = newRole.id;
      }
      await attachRole(accountId);
    } finally {
      proxy?.$loader?.hide();
    }
  };

  const attachRole = async (accountId) => {
    if (!state.attachRoleModal.selectedRoleId || !positions.getCurrentPosition()?.id) return;
    try {
      proxy?.$loader?.show();
      await positions.attachRole({
        accountId,
        positionId: positions.getCurrentPosition().id,
        roleId: state.attachRoleModal.selectedRoleId,
      });
      state.attachRoleModal.show = false;
      await positions.loadAllPositions();
      await roles.loadAllRoles();
    } catch (e) {
      console.error(e);
    } finally {
      proxy?.$loader?.hide();
    }
  };

  // Relieve Assignment Methods
  const getRelieveAssignmentAvatar = () => {
    const employee = state.relieveAssignment.assignedEmployee;
    if (!employee) return null;

    const employeeName = employee.assigned_employee_name || employee.employeeName || "";
    const nameParts = employeeName.split(" ");

    return {
      picture: getEmployeeAvatar(employee),
      firstName: nameParts[0] || "U",
      lastName: nameParts[1] || "S",
    };
  };

  const closeRelieveAssignment = () => {
    state.relieveAssignment.show = false;
    state.relieveAssignment.currentPosition = null;
    state.relieveAssignment.assignedEmployee = null;
  };

  /**
   * ER-12223: extracts a human-readable message from a relieve API error,
   * mirroring relievePrimaryPositionFromEmployee on the Employees view.
   * Backend errors arrive as an array (Django), a `detail` string, or a plain
   * string; anything else falls back to the default copy.
   */
  const getRelieveErrorMessage = (error) => {
    const responseData = error?.response?.data;
    return (
      (Array.isArray(responseData) && responseData.join(" ")) ||
      responseData?.detail ||
      (typeof responseData === "string" && responseData) ||
      "Failed to relieve the position assignment. Please try again."
    );
  };

  const relievePrimaryAssignment = async (accountId, effectiveTo) => {
    const position = state.relieveAssignment.currentPosition;
    if (state.relieveAssignment.type === "primary") {
      if (!position?.assigned_primary) return;
      proxy?.$loader?.setAppLoading(true);
      try {
        const idEmployee = position.assigned_primary.assigned_employee_id || null;
        const employeeData = await getSingleEmployeeV2(accountId, idEmployee);
        const assignmentId = employeeData?.data?.primary_position?.id || null;
        const employeeId = employeeData?.data?.id || null;

        await positions.relievePrimaryAssignment({ accountId, assignmentId, effectiveTo });
        await positions.loadAllPositions();
        await recalculateEmployeeReadiness(accountId, employeeId);
        closeRelieveAssignment();
        proxy?.$notifications?.success("Position unassigned successfully");
      } catch (error) {
        console.error("Error relieving primary assignment:", error);
        proxy?.$notifications?.error(getRelieveErrorMessage(error));
      } finally {
        proxy?.$loader?.setAppLoading(false);
      }
    } else if (state.relieveAssignment.type === "prepare") {
      if (!position?.assigned_preparing_for) return;
      proxy?.$loader?.setAppLoading(true);
      try {
        const idEmployee = state.relieveAssignment?.assignedEmployee?.assigned_employee_id || null;
        const employeeData = await getSingleEmployeeV2(accountId, idEmployee);
        const assignedPosition = employeeData?.data?.preparing_for_positions.find(
          (el) => el.code_str === position.code_str
        );
        const assignmentId = assignedPosition?.id;
        const employeeId = employeeData?.data?.id || null;
        await positions.relievePrimaryAssignment({
          accountId,
          assignmentId,
          employeeId,
          effectiveTo,
        });
        await positions.loadAllPositions();
        await recalculateEmployeeReadiness(accountId, employeeId);
        closeRelieveAssignment();
        proxy?.$notifications?.success("Position unassigned successfully");
      } catch (error) {
        console.error("Error relieving primary assignment:", error);
        proxy?.$notifications?.error(getRelieveErrorMessage(error));
      } finally {
        proxy?.$loader?.setAppLoading(false);
      }
    }
  };

  // Assignment Management
  const onRelievePrimaryAssignment = (position) => {
    // This will be handled by parent component via event emission
    // Store position for potential future use
    state.currentPosition = position;
  };

  const onRelievePrepareAssignment = (position) => {
    // This will be handled by parent component via event emission
    // Store position for potential future use
    state.currentPosition = position;
  };

  const onPreparingAvatarsClick = (position) => {
    // This will be handled by parent component via event emission
    state.currentPosition = position;
  };

  const onPrimaryEffectiveDateClick = async (effectiveDate, position) => {
    state.currentPosition = position;
    const employeeId = position?.assigned_primary?.assigned_employee_id;
    if (!employeeId || !effectiveDate) return;
    try {
      proxy?.$loader?.setAppLoading?.(true);
      const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
      const employeeData = await getSingleEmployeeV2(accountId, employeeId);
      const assignmentId = employeeData?.data?.primary_position?.id;
      if (!assignmentId) return;
      await updatePositionAssignment(accountId, { effective_on: effectiveDate }, assignmentId);
      await positions.loadAllPositions();
      await recalculateEmployeeReadiness(accountId, employeeData?.data?.id);
    } catch (e) {
      console.error("Error updating primary effective date:", e);
    } finally {
      proxy?.$loader?.setAppLoading?.(false);
    }
  };

  const onPreparingEffectiveDateClick = async (effectiveDate, position) => {
    state.currentPosition = position;
    const employeeId = position?.assigned_preparing_for?.[0]?.assigned_employee_id;
    if (!employeeId || !effectiveDate) return;
    try {
      proxy?.$loader?.setAppLoading?.(true);
      const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
      const employeeData = await getSingleEmployeeV2(accountId, employeeId);
      const preparingAssignment = employeeData?.data?.preparing_for_positions?.find(
        (el) => el?.code_str === position?.code_str || el?.position === position?.id
      );
      const assignmentId = preparingAssignment?.id;
      if (!assignmentId) return;
      await updatePositionAssignment(accountId, { effective_on: effectiveDate }, assignmentId);
      await positions.loadAllPositions();
      await recalculateEmployeeReadiness(accountId, employeeData?.data?.id);
    } catch (e) {
      console.error("Error updating preparing effective date:", e);
    } finally {
      proxy?.$loader?.setAppLoading?.(false);
    }
  };

  // UI Helpers
  const getStatusColor = (status) => {
    const colorMap = {
      active: "#10B981",
      draft: "#667085",
      retired: "#F79009",
      inactive: "#EF4444",
    };
    return colorMap[status] || "#667085";
  };

  const openPositionDetails = async (position) => {
    if (position?.id) {
      state.currentPosition = position;
      // Navigate to position details or emit event
      try {
        if (proxy?.$router) {
          await proxy.$router.push(`/organization/positions/position/${position.id}`);
        }
      } catch (e) {
        // In Storybook or other contexts, router might not be available
        console.warn("Router not available:", e);
      }
    }
  };

  // Chatter Operations
  const getPositionChatter = async () => {
    try {
      const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
      state.openChatterModal.show = true;
      const response = await getChatterList(
        accountID,
        state.openChatterModal.recordId,
        state.openChatterModal.type,
        state.openChatterModal.page
      );
      if (response) {
        let chatterItems = transformChatterData(response.data.results);
        state.openChatterModal.chatterData.push(...chatterItems);
        state.openChatterModal.totalPositionChatter = response.data.count;
      }
    } catch (error) {
      console.log("e: ", error);
    }
  };

  const openPositionChatterModal = async (
    item,
    type = "position",
    recordId = null,
    typeValue = "",
    isEmployeeChatter = false,
    avatar = null
  ) => {
    positions.setCurrentPosition(item);
    state.currentPosition = item;
    state.openChatterModal.chatterData = [];
    state.openChatterModal.recordId = recordId || item?.id;
    state.openChatterModal.type = type;
    state.openChatterModal.typeValue = typeValue || item?.name || "";
    state.openChatterModal.isEmployeeChatter = isEmployeeChatter;
    state.openChatterModal.avatar = avatar || {
      picture: "",
      firstName: "",
      lastName: "",
    };
    state.openChatterModal.page = 1;
    await getPositionChatter();
  };

  const closePositionChatterModal = () => {
    state.openChatterModal.chatterData = [];
    state.openChatterModal.totalPositionChatter = 0;
    state.openChatterModal.page = 1;
    state.openChatterModal.show = false;
  };

  const requestMoreChatter = () => {
    const page = Math.ceil(state.openChatterModal.chatterData.length / 10);
    state.openChatterModal.page = page + 1;
    getPositionChatter();
  };

  // Initialize filters from persistence; when nothing saved (or empty), use Draft + Active + Retired
  const getFilters = () => {
    const persistedFilters = getFiltersFromStorage(state.filters.key);
    const effective =
      Array.isArray(persistedFilters) && persistedFilters.length > 0
        ? resolveOrganizationPositionStatusFilters(persistedFilters)
        : [...DEFAULT_ORGANIZATION_POSITION_STATUS_FILTERS];
    state.filters.values = [...effective];
    const persistedMeta = getFilterMetaFromStorage(state.filters.key);
    if (persistedMeta && typeof persistedMeta === "object") {
      state.filters.department = persistedMeta.department || null;
      state.filters.departmentScope = persistedMeta.departmentScope || "subtree";
      state.filters.paneDepartment = persistedMeta.paneDepartment || null;
      state.filters.paneDepartmentScope = persistedMeta.paneDepartmentScope || "subtree";
    }
    if (!Array.isArray(persistedFilters) || persistedFilters.length === 0) {
      filter.setFilters({
        key: state.filters.key,
        filters: effective,
      });
    }
    pushFilterValuesToPositionsStore(effective);
  };

  /**
   * MDP-615 — re-read the Categorized view gates (shipping flag + per-column gates)
   * and the account toggles (job_categories_on / job_families_on), then re-resolve
   * the selection so a hidden segment falls back to Grid (Rule 1/10).
   *
   * ER-12338: the singleton short-circuits before onMounted, so a Settings toggle
   * was stale until a full reload. This is invoked both from initializeFilters and
   * from the per-mount hook registered on the singleton return path.
   */
  const refreshCategorizationSettings = async (proxy = null) => {
    // Called both from initializeFilters (which already captured `proxy` synchronously)
    // and from the per-mount hook on the singleton path (where getCurrentInstance is
    // still valid synchronously in onMounted). Never call getCurrentInstance after an
    // await — it returns null once the synchronous hook has finished.
    const flagProxy = proxy || getCurrentInstance()?.proxy;
    let flag615 = false;
    let flag566 = false;
    let flag581 = false;
    let flag440 = false;
    try {
      [flag615, flag566, flag581, flag440] = await Promise.all([
        flagProxy?.$getFlag?.(LD_FLAG_IDEA_MDP_615, false),
        flagProxy?.$getFlag?.(LD_FLAG_IDEA_MDP_566, false),
        flagProxy?.$getFlag?.(LD_FLAG_IDEA_MDP_581, false),
        flagProxy?.$getFlag?.(LD_FLAG_IDEA_MDP_440, false),
      ]);
    } catch {
      // A failing LD call must not break the mount — default every categorization
      // gate to off, matching the host component's own defensive flag reads.
    }
    state.categorization.ideaMdp615Enabled = flag615 === true;
    state.categorization.jobCategoryFlagEnabled = flag566 === true;
    state.categorization.jobFamilyFlagEnabled = flag581 === true;
    state.categorization.departmentFlagEnabled = flag440 === true;
    await loadCategorizationSettings();
    setPositionsViewSelection(state.config.positionsViewSelection);
  };

  const initializeFilters = async () => {
    const app = getCurrentInstance();
    const { proxy } = app || {};

    // MDP-551 (ER-10494): Validity / Occupied / Preparing-for radio groups are
    // new behaviour — hide them while the flag is off (the backend no-ops their
    // query params anyway). ER-11707: record the flag on state so
    // pushFilterValuesToPositionsStore applies the same gate to KPI-card clicks.
    const mdp551Enabled = (await proxy.$getFlag(LD_FLAG_IDEA_MDP_551, false)) === true;
    state.mdp551Enabled = mdp551Enabled;
    if (!mdp551Enabled) {
      state.filters.filterGroups = state.filters.filterGroups.filter(
        (group) => !["Validity", "Occupied", "Preparing for"].includes(group.label)
      );
    }

    if (!isProductReadinessEnabled()) {
      state.filters.filterGroups = filterPreparingForFromPositionFilterGroups(
        state.filters.filterGroups
      );
      state.filters.filterGroups = filterValidityFromPositionFilterGroups(
        state.filters.filterGroups
      );
      state.config.searchPositionByMultipleFields = getPositionMultiFieldSearchFields(
        state.config.searchPositionByMultipleFields
      );
      state.filters.values = stripPreparingForPositionFilterValues(state.filters.values);
      state.filters.values = stripValidityPositionFilterValues(state.filters.values);
      state.filters.values = stripRolesAttachedPositionFilterValues(state.filters.values);
      positions.removePositionFilter("preparing_for");
      positions.removePositionFilter("preparing_employee_name");
      positions.removePositionFilter("validity");
      positions.removePositionFilter("roles_attached");
      state.config.positionsViewSelection = normalizeOrganizationPositionsViewSelection(
        state.config.positionsViewSelection
      );
    }

    // Check feature flag for hiding archived filter
    const hideArchivedFilterPositions = await proxy.$getFlag(
      "hide_archived_filter_positions",
      false
    );

    if (hideArchivedFilterPositions) {
      // Remove archived filter from filterGroups
      state.filters.filterGroups = state.filters.filterGroups.filter(
        (group) => group.label !== "Archived"
      );
    }

    // MDP-615 — Categorized view gates (shipping flag + per-column gates) and the
    // account toggles, then re-resolve the selection so a hidden segment falls
    // back to Grid. Shared with the per-mount refresh on the singleton path.
    await refreshCategorizationSettings(proxy);
  };

  const handleMultipleFieldSearch = async (event) => {
    POSITION_LIST_SEARCH_PARAM_KEYS.forEach((key) => {
      positions.removePositionFilter(key);
    });

    // Add filters for fields that have non-empty values
    if (event && Array.isArray(event)) {
      event.forEach((field) => {
        if (field.value && field.value.trim() !== "") {
          positions.addPositionFilter(field.fieldKey, field.value);
        }
      });
    }

    // Update state with the search values
    state.filters.multipleFieldSearchValues = event || [];

    // Reset pagination
    positions.setPaginationDataOffset(0);
    state.pagination.page = 1;

    // Load positions with the new filters
    await positions.loadAllPositions();
  };

  onMounted(async () => {
    await initializeFilters();
    getFilters();
    // applyFilters(
    //   state.filters.values.map((value) => ({ label: value, value: value }))
    // );
  });

  instance = {
    state,
    config: state.config,
    applyFilters,
    pushFilterValuesToPositionsStore,
    setDepartmentFilter,
    removeFilters,
    clearFilters,
    clearSearch,
    handleSearch,
    handleRequest,
    handleSort,
    handlePositionDropdownOperation,
    handlePositionOperation,
    onRelievePrimaryAssignment,
    onRelievePrepareAssignment,
    onPreparingAvatarsClick,
    onPrimaryEffectiveDateClick,
    onPreparingEffectiveDateClick,
    getStatusColor,
    openPositionDetails,
    handleCapacityChange,
    openPositionChatterModal,
    closePositionChatterModal,
    requestMoreChatter,
    getFilters,
    // Attach Role methods
    prepareAttachRole,
    getListOfRoles,
    loadMoreRoles,
    searchRoles,
    handleRoleSelected,
    handleAddRoleSelected,
    attachRole,
    // Relieve Assignment methods
    getRelieveAssignmentAvatar,
    closeRelieveAssignment,
    relievePrimaryAssignment,
    // Positions reference
    positions,
    handleMultipleFieldSearch,
    // MDP-615 — Categorized view
    hasCategorizationColumns,
    hasCategorizedView,
    setPositionsViewSelection,
    setCanEditPositions,
    isRowReadOnly,
    loadCategorizationSettings,
    refreshCategorizationSettings,
    reloadCategorizedPositions,
    getCategorizedDepartment,
    getCategorizedJobCategory,
    getCategorizedJobFamily,
    getCategorizedJobFamilyLevel,
    openCategorizedDepartmentPicker,
    closeCategorizedDepartmentPicker,
    assignCategorizedDepartment,
    unassignCategorizedDepartment,
    openCategorizedJobCategoryPicker,
    closeCategorizedJobCategoryPicker,
    loadCategorizedJobCategories,
    assignCategorizedJobCategory,
    unassignCategorizedJobCategory,
    openCategorizedJobFamilyPicker,
    closeCategorizedJobFamilyPicker,
    loadCategorizedJobFamilies,
    assignCategorizedJobFamily,
    unassignCategorizedJobFamily,
    openCategorizedUnassignDialog,
    closeCategorizedUnassignDialog,
    confirmCategorizedUnassign,
  };

  return instance;
}

export function resetOrganizationPositionsViewState() {
  instance = null;
}
